import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { GraduationCap, Wrench, Cpu, ArrowRight } from "lucide-react";
import { PageHero, SectionHead, Reveal } from "@/components/bits/Reveal";
import InternshipForm from "@/components/forms/InternshipForm";
import { IMAGES, INTERNSHIP_PROGRAMS, INTERNSHIP_PROCESS } from "@/data/content";
import { useSEO } from "@/lib/seo";

const WHO = [
  { icon: GraduationCap, title: "B.Tech Students", text: "Industrial internships and graduate exposure programs that connect engineering coursework to real production environments." },
  { icon: Wrench, title: "Diploma Students", text: "Industrial training inside manufacturing plants — machines, processes, quality systems and workplace discipline." },
  { icon: Cpu, title: "ITI Students", text: "Trade-linked training and apprenticeship-style exposure for fitters, electricians, machinists and other trades." },
];

const GAINS = ["Industry exposure", "Practical learning", "Workplace understanding", "Technical exposure", "Professional communication", "Career awareness", "Industry interaction", "Placement support where applicable"];

export default function Internships() {
  useSEO(
    "Internships & Industrial Training | Maqline Solutions Pvt Ltd — B.Tech, Diploma & ITI",
    "Maqline Solutions Pvt Ltd offers industry-focused internships and industrial training in Bengaluru for B.Tech, Diploma and ITI students — industrial exposure, practical learning and career guidance."
  );
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [hash]);

  return (
    <main data-testid="internships-page">
      <PageHero
        overline="Internships & Industrial Training"
        titleLines={["Learn. Experience. Grow."]}
        description="Industry-focused internship and training opportunities for students looking to understand the real workplace."
        image={IMAGES.audienceStudents}
      />

      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="Who Can Apply" title="Built for students of industry" />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {WHO.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.1}>
                <div className="group border border-ink/10 hover:border-gold/50 rounded-sm p-8 h-full transition-all duration-400 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(6,27,58,0.08)]" data-testid={`internship-who-${i}`}>
                  <div className="w-12 h-12 bg-navy/5 border border-gold/30 rounded-sm flex items-center justify-center mb-6 group-hover:bg-gold/10 transition-colors duration-300">
                    <w.icon size={22} className="text-gold" />
                  </div>
                  <h3 className="font-display text-2xl text-ink mb-3">{w.title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-darknavy noise-bg py-16 sm:py-24" id="training">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHead dark overline="What You Gain" title={["Training that employers", "actually respect."]} description="Industrial Training for Diploma & ITI Students — and internships for B.Tech students — designed around what real workplaces need." />
            <Reveal delay={0.2}>
              <div className="mt-9 grid grid-cols-2 gap-2.5">
                {GAINS.map((g) => (
                  <div key={g} className="flex items-center gap-2.5 text-sm text-white/75">
                    <span className="w-1.5 h-1.5 bg-gold rotate-45 shrink-0" /> {g}
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-9 border border-gold/30 bg-gold/5 rounded-sm p-6">
                <p className="text-lightgold font-semibold text-sm">Stipend opportunities up to ₹18,000–₹23,000</p>
                <p className="text-white/50 text-xs mt-2 leading-relaxed">Stipend eligibility and amount may vary depending on the program, role, organization and applicable terms. A stipend is not guaranteed for every program.</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.12}>
            <div className="relative">
              <img src={IMAGES.audienceColleges} alt="Student in industrial training" loading="lazy" className="w-full h-[460px] object-cover rounded-sm" />
              <div className="absolute inset-0 border border-gold/30 rounded-sm -translate-x-4 -translate-y-4 -z-10" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-steel py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="Programs" title="Five ways to step into industry" />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INTERNSHIP_PROGRAMS.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.08}>
                <div className="bg-white border border-ink/10 hover:border-gold/50 rounded-sm p-7 h-full transition-colors duration-400 group" data-testid={`program-${i}`}>
                  <span className="font-mono text-xs text-gold font-bold">0{i + 1}</span>
                  <h3 className="font-display text-xl text-ink mt-3 mb-2 group-hover:text-navy transition-colors duration-300">{p.title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="The Process" title="From application to completion" />
          <div className="mt-16 relative">
            <div className="absolute left-[19px] lg:left-0 lg:top-[19px] top-0 lg:w-full lg:h-px w-px h-full bg-gradient-to-b lg:bg-gradient-to-r from-gold/60 via-gold/25 to-transparent" />
            <div className="grid grid-cols-1 lg:grid-cols-6 gap-10 lg:gap-4">
              {INTERNSHIP_PROCESS.map((s, i) => (
                <Reveal key={s.num} delay={i * 0.09}>
                  <div className="relative pl-14 lg:pl-0 lg:pt-14" data-testid={`internship-step-${s.num}`}>
                    <div className="absolute left-0 lg:left-0 top-0 lg:-top-[0px] w-10 h-10 lg:translate-y-[-0px] bg-darknavy border border-gold/50 rounded-sm flex items-center justify-center font-mono text-sm font-bold text-lightgold">{s.num}</div>
                    <h3 className="font-display text-lg text-ink mb-1.5">{s.title}</h3>
                    <p className="text-[13px] text-muted leading-relaxed">{s.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="apply" className="bg-steel py-16 sm:py-24 scroll-mt-20" data-testid="internship-apply-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <SectionHead overline="Apply Now" title="Start your industry journey" description="Submit your application — our team will review your profile and match you to a suitable program." />
          <Reveal delay={0.15}>
            <div className="mt-12 border border-ink/10 rounded-sm p-6 sm:p-10 bg-white">
              <InternshipForm />
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-center text-xs text-muted mt-6">
              Representing a college? <a href="/colleges" className="text-navy font-semibold hover:text-gold transition-colors inline-flex items-center gap-1">Partner through our TPO program <ArrowRight size={12} /></a>
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
